/**
 * Protected Route Component (Phase 11)
 * =====================================
 * Wrapper for routes that require authentication
 */

import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { getTokens, autoRefreshToken } from '../api/auth'

interface ProtectedRouteProps {
  children: ReactNode
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    const tokens = getTokens()

    if (!tokens) {
      setIsAuthenticated(false)
      return
    }

    // Try to refresh token if expired
    const validToken = await autoRefreshToken()

    setIsAuthenticated(!!validToken)
  }

  // Loading state
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Checking authentication...</p>
        </div>
      </div>
    )
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Authenticated
  return <>{children}</>
}
